import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@shared/components/ui/button';
import { Badge } from '@shared/components/ui/badge';
import { ENV } from '@shared/config/env';
import {
  X,
  HelpCircle,
  ChevronRight,
  ChevronDown,
  LayoutDashboard,
  FolderKanban,
  ClipboardCheck,
  QrCode,
  ShieldAlert,
  Users,
  Building2,
  ScanLine,
  BarChart3,
  AlertTriangle,
  Settings,
  Search,
  Printer,
  BookOpen,
  UserPlus,
  FileCheck,
  ListChecks,
  Shield,
} from 'lucide-react';

const HelpModal = ({ isOpen, onClose, userRole }) => {
  const [expandedSection, setExpandedSection] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  const toggleSection = (section) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  const handlePrint = () => {
    const sections = allSections;
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Help & User Guide - Depot Induction Admin</title>
          <style>
            body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; padding: 40px; color: #333; }
            h1 { font-size: 24px; margin-bottom: 8px; }
            h2 { font-size: 18px; margin-top: 24px; margin-bottom: 12px; padding-bottom: 6px; border-bottom: 2px solid #e5e7eb; }
            h3 { font-size: 14px; margin-top: 12px; margin-bottom: 4px; }
            p { font-size: 13px; color: #555; margin-bottom: 8px; }
            .section { margin-bottom: 20px; }
            @media print { body { padding: 20px; } }
          </style>
        </head>
        <body>
          <h1>Help & User Guide</h1>
          <p style="color: #888;">Depot Induction & Site Access Management</p>
    `);
    sections.forEach(section => {
      printWindow.document.write(`<div class="section"><h2>${section.title}</h2>`);
      section.content.forEach(item => {
        printWindow.document.write(`<h3>${item.heading}</h3><p>${item.text}</p>`);
      });
      printWindow.document.write('</div>');
    });
    printWindow.document.write('</body></html>');
    printWindow.document.close();
    printWindow.print();
  };

  // Define guide sections for depot induction admin
  const getGuideSections = () => {
    const commonSections = [
      {
        id: 'dashboard',
        title: 'Home Dashboard',
        icon: LayoutDashboard,
        content: [
          { heading: 'Today at a Glance', text: 'The home dashboard shows four key performance cards: Currently On Site (live occupancy with employee/contractor/visitor breakdown), Pending Approvals, Open Incidents, and Today\'s Check-ins.' },
          { heading: 'Quick Actions', text: 'Use the quick-action buttons to generate QR codes, review pending approvals, or open the kiosk scanner directly from the dashboard.' },
          { heading: 'Navigation', text: 'Use the sidebar menu to navigate between sections. On mobile, tap the menu icon to access navigation.' },
          { heading: 'Hazard Alerts', text: 'When medium, high, or critical severity incidents are open, a hazard alert banner appears automatically in the header. Click it to view all active hazards.' },
        ]
      },
      {
        id: 'projects',
        title: 'Projects Management',
        icon: FolderKanban,
        content: [
          { heading: 'Creating Projects', text: 'Click "Create Project" to set up a new project. Enter the project name, summary, select the site/depot, set start and end dates, and provide the project lead\'s details (name, email, phone, company).' },
          { heading: 'Project Workflow', text: 'Projects flow through: Draft → Pending Supplier → In Progress → Submitted → Approved/Rejected → Active → Completed. Each status indicates where the project is in the approval pipeline.' },
          { heading: 'Status Filtering', text: 'Use the status filter buttons at the top to quickly view projects by state: All, Submitted (awaiting review), Approved, Active, or Rejected.' },
          { heading: 'Reviewing Submissions', text: 'When a supplier submits a project for review, click into the project to see uploaded documents, team member induction status, and project details.' },
          { heading: 'Document Review', text: 'View each uploaded document (RAMS, Method Statements, Insurance, Permits) via the View button. Download documents or print them. Use Accept/Reject buttons to approve or request re-upload of each document.' },
          { heading: 'Team Members', text: 'Review the list of team members, their roles, and their induction completion status. All team members must complete site inductions before the project can be fully approved.' },
          { heading: 'Approving Projects', text: 'Once you\'re satisfied with documents and induction completion, click "Approve". This automatically generates QR passes for the project lead and all team members, and sends pass emails with QR codes.' },
          { heading: 'Rejecting Projects', text: 'If documentation is insufficient, click "Reject" and provide a reason. The project lead will be notified and can amend and resubmit.' },
        ]
      },
      {
        id: 'analytics',
        title: 'Analytics Dashboard',
        icon: BarChart3,
        content: [
          { heading: 'Time Range', text: 'Filter analytics by time period: Last 24 Hours, Last 7 Days, or Last 30 Days using the buttons at the top.' },
          { heading: 'Key Metrics', text: 'View KPIs including currently on site count, today\'s total visitors, and average session duration.' },
          { heading: 'Charts', text: 'Interactive charts show daily check-in trends (bar chart), occupancy patterns over time (line chart), and person type breakdown (pie chart — employees, contractors, visitors).' },
          { heading: 'Data Export', text: 'Charts and data can be used for compliance reporting and site safety reviews.' },
        ]
      },
      {
        id: 'emergency',
        title: 'Emergency Roll Call',
        icon: AlertTriangle,
        content: [
          { heading: 'Purpose', text: 'The Emergency Roll Call provides a live, real-time list of every person currently on site. Use this during fire drills, evacuations, or emergency situations.' },
          { heading: 'Information Shown', text: 'Each entry shows person name, company, person type, check-in time, and work area.' },
          { heading: 'Search & Filter', text: 'Use the search bar to quickly find specific individuals. Filter by person type if needed.' },
          { heading: 'Auto-Refresh', text: 'The roll call refreshes automatically every 10 seconds to ensure accuracy.' },
          { heading: 'Export & Print', text: 'Click Export to download the roll call as a CSV file. Use the Print button to print the current list for physical records.' },
        ]
      },
      {
        id: 'kiosk',
        title: 'QR Sign In/Out (Kiosk)',
        icon: ScanLine,
        content: [
          { heading: 'Setting Up', text: 'The kiosk is designed for iPad/tablet use at site entry points. Open the Kiosk page and authenticate with the kiosk PIN.' },
          { heading: 'Check-In Mode', text: 'In check-in mode, persons present their QR code to the camera. The system validates the pass (checks active status, valid dates, correct depot).' },
          { heading: 'Fitness Attestation', text: 'During check-in, the person must confirm they are fit for work (no impairment) and acknowledge PPE requirements before entry is granted.' },
          { heading: 'Check-Out Mode', text: 'Switch to check-out mode for persons leaving site. They scan their QR code and the system records the check-out time.' },
          { heading: 'Success/Error Feedback', text: 'After each scan, a success or error screen is displayed showing person details, company, and timestamp. An audible beep confirms the scan.' },
          { heading: 'Occupancy Tracking', text: 'All sign-in/out data feeds into the Emergency Roll Call, occupancy counts on the dashboard, and analytics.' },
        ]
      },
      {
        id: 'incidents',
        title: 'Incident Reporting',
        icon: ShieldAlert,
        content: [
          { heading: 'Creating Reports', text: 'Click "Report Incident" to create a new incident. Fill in the title, description, category (Safety, Environmental, Security, Near-Miss), severity (Low, Medium, High, Critical), location, work area, and date/time.' },
          { heading: 'People Involved', text: 'Add the names and details of anyone involved in or witnessing the incident.' },
          { heading: 'Photo Evidence', text: 'Upload photos of the incident from your device or take photos directly using the camera.' },
          { heading: 'Anonymous Reporting', text: 'Incidents can be reported anonymously — the reporter\'s identity will not be recorded.' },
          { heading: 'Managing Incidents', text: 'Filter incidents by status: Open, Investigating, Resolved, or Closed. Click on an incident to view full details.' },
          { heading: 'Resolving Incidents', text: 'When an incident has been addressed, mark it as Resolved with closure notes documenting the actions taken.' },
          { heading: 'Hazard Banner', text: 'Open incidents with Medium, High, or Critical severity automatically trigger a hazard warning banner visible across the admin app.' },
        ]
      },
    ];

    const adminSections = [
      {
        id: 'induction-approvals',
        title: 'Site Approvals',
        icon: ClipboardCheck,
        content: [
          { heading: 'Approval Queue', text: 'The Site Approvals page shows all pending induction submissions waiting for review. Search by name, email, or company.' },
          { heading: 'Reviewing Submissions', text: 'Click a submission to review the person\'s answers to the induction brief questions. Check that all required information has been provided correctly.' },
          { heading: 'Approving', text: 'Click "Approve" to accept the submission. This automatically generates a QR pass for the person and sends them an email with their QR code and pass details.' },
          { heading: 'Rejecting', text: 'Click "Reject" and add notes explaining what needs to be corrected. The person will be notified and can resubmit.' },
          { heading: 'Daily Counts', text: 'The page shows counters for how many submissions have been approved and rejected today.' },
        ]
      },
      {
        id: 'qr-passes',
        title: 'QR Pass Generator',
        icon: QrCode,
        content: [
          { heading: 'Individual Pass', text: 'Generate a pass for a single person. Enter their name, email, company, person type, and validity dates. A unique QR token is created.' },
          { heading: 'Bulk Generation', text: 'Select multiple people and generate passes for all of them in one batch operation.' },
          { heading: 'CSV Import', text: 'For large numbers of passes, upload a CSV file with person details to create passes in bulk.' },
          { heading: 'Managing Passes', text: 'Search and filter existing passes. View or print QR codes. Copy pass tokens for sharing.' },
          { heading: 'Pass Validity', text: 'Each pass has start and end dates. Expired passes are automatically rejected at the kiosk.' },
        ]
      },
      {
        id: 'visitor-invites',
        title: 'Site Invites',
        icon: UserPlus,
        content: [
          { heading: 'Sending Invitations', text: 'Create a site invite by entering the person\'s name, email, company, and selecting the site. An email is sent with a unique link to complete the site induction.' },
          { heading: 'Invite Tracking', text: 'Monitor invite statuses: Sent, Opened, Submitted, Approved. See when the person last interacted with the invitation.' },
          { heading: 'Resending', text: 'If a person hasn\'t responded, use the Resend button to send a reminder email with the same invitation link.' },
          { heading: 'Filtering', text: 'Filter invitations by status to quickly see who still needs follow-up.' },
        ]
      },
      {
        id: 'induction-briefs',
        title: 'Induction Briefs',
        icon: BookOpen,
        content: [
          { heading: 'What are Briefs?', text: 'Induction briefs are the content that contractors and visitors must read and complete before being granted site access. They contain safety information, site rules, and compliance questions.' },
          { heading: 'Brief Types', text: 'Three types of brief: Questions (interactive form with text, checkbox, radio, and select questions), PDF (upload a document for reading), and Video (upload a safety video).' },
          { heading: 'Creating a Brief', text: 'Click "Create Brief" and enter the title, description, and select the type. For question-based briefs, add sections with different question types. Toggle the "Mandatory" flag if completion is required for site access.' },
          { heading: 'Depot Assignment', text: 'Assign briefs to specific depots or all sites. Only relevant briefs will be shown to visitors for each depot.' },
          { heading: 'Versioning', text: 'Briefs support versioning. When you update content, a new version is created while preserving the original.' },
          { heading: 'Active/Inactive', text: 'Toggle briefs between active and inactive. Inactive briefs won\'t be shown to new visitors but historical submissions are preserved.' },
          { heading: 'Preview & Duplicate', text: 'Preview how a brief looks to visitors before publishing. Duplicate existing briefs to create variations quickly.' },
        ]
      },
      {
        id: 'audits',
        title: 'Auditing',
        icon: ListChecks,
        content: [
          { heading: 'My Audits', text: 'View your ongoing and completed audits. Audits can be in Draft, In Progress, or Submitted status.' },
          { heading: 'Starting an Audit', text: 'Create a new audit by selecting an audit template. The template defines what sections and items you\'ll check during the audit.' },
          { heading: 'Audit Runner', text: 'The mobile-first audit runner guides you through each section. Check items, mark findings, add photo evidence, and track your progress with the completion bar.' },
          { heading: 'Findings', text: 'When you identify an issue, a finding form auto-expands to capture details. Add severity, description, and photo evidence.' },
          { heading: 'Submitting', text: 'Once all sections are complete, submit the audit for record. Submitted audits are stored with a full audit trail.' },
        ]
      },
      {
        id: 'audit-templates',
        title: 'Audit Templates',
        icon: FileCheck,
        content: [
          { heading: 'Creating Templates', text: 'Build audit templates with a name, code, description, and audit type (e.g., safety tour). Assign templates to specific depots.' },
          { heading: 'Template Builder', text: 'Use the template builder to add sections with ordered checklist items. Each item defines what the auditor needs to check.' },
          { heading: 'Scoring', text: 'Configure pass threshold percentages for audit scoring. This determines the minimum compliance percentage needed.' },
          { heading: 'Publishing', text: 'Preview templates before publishing. Published templates create a new version and become available for conducting audits.' },
        ]
      },
      {
        id: 'user-management',
        title: 'User Management',
        icon: Users,
        content: [
          { heading: 'Managing Users', text: 'View and manage admin user accounts. Add new users with appropriate roles.' },
          { heading: 'Roles', text: 'Users can be assigned roles that determine their access level: Depot Manager has full admin access, while other roles may have limited permissions.' },
          { heading: 'Access Control', text: 'Admin and Depot Manager roles can access all management features. Other roles may only view dashboards and reports.' },
        ]
      },
      {
        id: 'site-management',
        title: 'Site / Depot Management',
        icon: Building2,
        content: [
          { heading: 'Managing Depots', text: 'Navigate to Settings → Depots to view and manage your sites. You can configure up to 3 depot locations.' },
          { heading: 'Depot Details', text: 'Set the depot name, unique code, and full address for each site. These details appear on passes and induction briefs.' },
          { heading: 'Adding / Editing', text: 'Click "Add Depot" to create a new site or click an existing depot to edit its details.' },
        ]
      },
      {
        id: 'system-settings',
        title: 'System Settings',
        icon: Settings,
        content: [
          { heading: 'Company Branding', text: 'Upload your company logo which appears in the admin app header AND is automatically shared with the Supplier Portal login page and sidebar.' },
          { heading: 'Depot Name', text: 'Set your organisation\'s depot name which is displayed throughout the system.' },
          { heading: 'Logo Storage', text: 'Logos are securely stored and served from cloud storage. Supported formats: PNG, JPG, SVG.' },
          { heading: 'Work Areas', text: 'Configure work areas and zones within each depot for location-specific tracking (under Settings → Work Areas).' },
          { heading: 'Notifications', text: 'Manage email notification preferences (under Settings → Notifications).' },
        ]
      },
    ];

    // Build sections based on role
    let sections = [...commonSections];

    if (['admin', 'depot_manager', 'owner', 'system-admin'].includes(userRole)) {
      sections = [...sections, ...adminSections];
    }

    return sections;
  };

  const allSections = getGuideSections();

  // Filter sections by search query
  const sections = useMemo(() => {
    if (!searchQuery.trim()) return allSections;
    const query = searchQuery.toLowerCase();
    return allSections.filter(section => {
      if (section.title.toLowerCase().includes(query)) return true;
      return section.content.some(
        item =>
          item.heading.toLowerCase().includes(query) ||
          item.text.toLowerCase().includes(query)
      );
    });
  }, [allSections, searchQuery]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        className="relative bg-card border border-border rounded-xl shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden mx-4"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border bg-muted/30">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              <HelpCircle className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h2 className="text-lg font-semibold">Help & User Guide</h2>
              <p className="text-sm text-muted-foreground">
                Depot Induction & Site Access
                {userRole && (
                  <Badge variant="outline" className="ml-2 capitalize text-xs">{userRole?.replace(/[-_]/g, ' ')}</Badge>
                )}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="icon" onClick={handlePrint} title="Print guide">
              <Printer className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="icon" onClick={onClose}>
              <X className="w-5 h-5" />
            </Button>
          </div>
        </div>

        {/* Search Bar */}
        <div className="px-4 pt-3 pb-2 border-b border-border">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search topics..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-4 py-2 text-sm bg-muted/50 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/50"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
          {searchQuery && (
            <p className="text-xs text-muted-foreground mt-1.5">
              {sections.length} {sections.length === 1 ? 'topic' : 'topics'} found
            </p>
          )}
        </div>

        {/* Content */}
        <div id="help-guide-content" className="overflow-y-auto max-h-[calc(85vh-180px)] p-4">
          {sections.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Search className="w-10 h-10 mx-auto mb-3 opacity-40" />
              <p className="font-medium">No topics found</p>
              <p className="text-sm mt-1">Try a different search term</p>
            </div>
          ) : (
            <div className="space-y-2">
              {sections.map((section) => (
                <div key={section.id} className="border border-border rounded-lg overflow-hidden">
                  <button
                    onClick={() => toggleSection(section.id)}
                    className="w-full flex items-center justify-between p-4 hover:bg-muted/50 transition-colors text-left"
                  >
                    <div className="flex items-center gap-3">
                      <section.icon className="w-5 h-5 text-muted-foreground" />
                      <span className="font-medium">{section.title}</span>
                    </div>
                    {expandedSection === section.id ? (
                      <ChevronDown className="w-5 h-5 text-muted-foreground" />
                    ) : (
                      <ChevronRight className="w-5 h-5 text-muted-foreground" />
                    )}
                  </button>
                  
                  <AnimatePresence>
                    {expandedSection === section.id && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="overflow-hidden"
                      >
                        <div className="px-4 pb-4 space-y-4 border-t border-border bg-muted/20">
                          <div className="pt-4 space-y-3">
                            {section.content.map((item, index) => (
                              <div key={index}>
                                <h4 className="font-medium text-sm text-foreground">{item.heading}</h4>
                                <p className="text-sm text-muted-foreground mt-1">{item.text}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ))}
            </div>
          )}

          {/* Quick Reference sections only shown when not searching */}
          {!searchQuery && (
            <>
              {/* Project Status Quick Reference */}
              <div className="mt-6 p-4 bg-blue-500/5 rounded-lg border border-blue-500/20">
                <h3 className="font-medium text-blue-600 dark:text-blue-400 mb-3 flex items-center gap-2">
                  <Shield className="w-4 h-4" />
                  Project Status Quick Reference
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-sm">
                  {[
                    { label: 'Draft', color: 'bg-gray-200 text-gray-700' },
                    { label: 'Pending Supplier', color: 'bg-yellow-100 text-yellow-700' },
                    { label: 'In Progress', color: 'bg-blue-100 text-blue-700' },
                    { label: 'Submitted', color: 'bg-purple-100 text-purple-700' },
                    { label: 'Approved', color: 'bg-green-100 text-green-700' },
                    { label: 'Rejected', color: 'bg-red-100 text-red-700' },
                    { label: 'Active', color: 'bg-emerald-100 text-emerald-700' },
                    { label: 'Completed', color: 'bg-slate-100 text-slate-700' },
                    { label: 'Archived', color: 'bg-gray-100 text-gray-500' },
                  ].map((status) => (
                    <div key={status.label} className="flex items-center gap-2">
                      <Badge className={`text-xs ${status.color}`}>{status.label}</Badge>
                    </div>
                  ))}
                </div>
              </div>

              {/* Incident Severity Reference */}
              <div className="mt-4 p-4 bg-orange-500/5 rounded-lg border border-orange-500/20">
                <h3 className="font-medium text-orange-600 dark:text-orange-400 mb-3 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  Incident Severity Levels
                </h3>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">Low</Badge>
                    <span className="text-muted-foreground">Minor issue, no hazard</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-xs bg-yellow-50 text-yellow-700 border-yellow-200">Medium</Badge>
                    <span className="text-muted-foreground">Potential hazard</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-xs bg-orange-50 text-orange-700 border-orange-200">High</Badge>
                    <span className="text-muted-foreground">Serious risk</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-xs bg-red-50 text-red-700 border-red-200">Critical</Badge>
                    <span className="text-muted-foreground">Immediate danger</span>
                  </div>
                </div>
              </div>

              {/* Quick Tips */}
              <div className="mt-4 p-4 bg-primary/5 rounded-lg border border-primary/20">
                <h3 className="font-medium text-primary mb-2">Quick Tips</h3>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• Use the Emergency Roll Call during drills or real emergencies to see who is on site</li>
                  <li>• Review project documents before approving — Accept or Reject each document individually</li>
                  <li>• Set induction briefs to "Mandatory" so visitors cannot generate a pass without completing them</li>
                  <li>• The kiosk fitness attestation ensures only fit workers enter the site</li>
                  <li>• Use the Hazard Alert banner to stay aware of open incidents</li>
                  <li>• CSV import makes it fast to create passes for large groups</li>
                </ul>
              </div>

              {/* Keyboard Shortcuts */}
              <div className="mt-4 p-4 bg-muted/30 rounded-lg border border-border hidden md:block">
                <h3 className="font-medium mb-2">Keyboard Shortcuts</h3>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="flex items-center gap-2">
                    <kbd className="px-2 py-1 bg-muted rounded text-xs">Esc</kbd>
                    <span className="text-muted-foreground">Close modals</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <kbd className="px-2 py-1 bg-muted rounded text-xs">?</kbd>
                    <span className="text-muted-foreground">Open this guide</span>
                  </div>
                </div>
              </div>

              {/* Version Info */}
              <div className="mt-4 text-center text-xs text-muted-foreground">
                Depot Induction v{ENV.APP.VERSION} • Site Access Management System
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-border bg-muted/30">
          <Button onClick={onClose} className="w-full">
            Close
          </Button>
        </div>
      </motion.div>
    </div>
  );
};

export default HelpModal;
